import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Sparkles, Users, Save, Clock, LogIn, Lightbulb } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

interface ActivityUsageGuideProps {
  isOpen: boolean;
  onClose: () => void;
  activityName: string;
  isAuthenticated: boolean;
}

const ActivityUsageGuide = ({
  isOpen,
  onClose,
  activityName,
  isAuthenticated,
}: ActivityUsageGuideProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();

  const handleSignup = () => {
    onClose();
    navigate("/register", {
      state: {
        from: location,
      },
    });
  };

  const handleLogin = () => {
    onClose();
    navigate("/login", {
      state: {
        from: location,
      },
    });
  };

  const features = [
    {
      icon: <Sparkles className="w-5 h-5 text-primary" />,
      text: t("usage_guide_immerse"),
    },
    {
      icon: <Users className="w-5 h-5 text-primary" />,
      text: t("usage_guide_mentoring"),
    },
    {
      icon: <Save className="w-5 h-5 text-primary" />,
      text: t("usage_guide_autosave"),
    },
    {
      icon: <Clock className="w-5 h-5 text-primary" />,
      text: t("usage_guide_access_later"),
    },
    {
      icon: <Lightbulb className="w-5 h-5 text-primary" />,
      text: t("usage_guide_insights"),
    },
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl">{t("usage_guide_title")}</DialogTitle>
          <DialogDescription className="text-sm">
            {t("usage_guide_description", { activityName })}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-6">
          {/* Features List */}
          <div className="space-y-3">
            {features.map((feature, index) => (
              <div key={index} className="flex items-start gap-3">
                <div className="mt-0.5">{feature.icon}</div>
                <p className="text-sm text-muted-foreground">{feature.text}</p>
              </div>
            ))}
          </div>

          {/* Signup Section - Only for non-authenticated users */}
          {!isAuthenticated && (
            <div className="p-4 border border-primary/20 rounded-lg bg-primary/5 dark:bg-indigo-950 dark:border-primary/80">
              <div className="flex items-start gap-3">
                <LogIn className="h-5 w-5 text-primary dark:text-primary/70 mt-0.5" />
                <div className="flex-1 space-y-2">
                  <p className="text-sm font-medium text-primary dark:text-primary-foreground/80">
                    {t("usage_guide_save_progress")}
                  </p>
                  <p className="text-xs text-primary dark:text-primary/60">
                    {t("usage_guide_signup_message")}
                  </p>
                  <div className="flex gap-2 mt-2">
                    <Button
                      onClick={handleSignup}
                      size="sm"
                      variant="default"
                    >
                      {t("sign_up")}
                    </Button>
                    <Button
                      onClick={handleLogin}
                      size="sm"
                      variant="outline"
                      className="text-primary border-primary/30 hover:bg-primary/10 dark:border-indigo-700 dark:text-primary/60 dark:hover:bg-primary"
                    >
                      {t("login")}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Privacy Notice */}
          <div className="pt-4 space-y-2 border-t">
            <p className="text-xs text-muted-foreground">
              <strong>{t("usage_guide_privacy_notice")}:</strong> {t("usage_guide_privacy_message")}
            </p>
            <a
              href="/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block text-xs text-primary hover:underline"
            >
              {t("usage_guide_privacy_link")} {i18n.dir() === 'rtl' ? '←' : '→'}
            </a>
          </div>
        </div>

        <div className="flex justify-end">
          <Button onClick={onClose} variant="default">
            {t("got_it")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ActivityUsageGuide;
